import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BrandLogo, Button, GoogleSignInButton, PasswordInput, Stack, TextInput } from '@fluent/ui';
import { supabase } from '../lib/supabase.js';
import { supabaseAuthErrorKey } from '../auth/errors.js';
import { signInWithGoogle as signInWithGoogleEverywhere } from '../auth/googleSignIn.js';
import { isInAppBrowser } from '../pwa/installPromptStore.js';
import { useUrlAuthError } from '../auth/useUrlAuthError.js';
import { LegalLinks } from '../legal/LegalLinks.js';
import './auth.css';

export function LogIn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string; form?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  const oauthError = useUrlAuthError();
  useEffect(() => {
    if (oauthError) setErrors((prev) => ({ ...prev, form: oauthError }));
  }, [oauthError]);

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    const e: typeof errors = {};
    if (!email) e.email = t('auth.errors.email_required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = t('auth.errors.invalid_email');
    if (!password) e.password = t('auth.errors.password_required');
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);

    if (error) {
      setErrors({ form: t(supabaseAuthErrorKey(error)) });
      return;
    }
    navigate(from, { replace: true });
  };

  // Google refuses sign-in inside Facebook / Instagram / TikTok's own
  // browser (see SignUp) — don't offer a button that can only fail there.
  const [inApp] = useState(isInAppBrowser);

  const signInWithGoogle = async () => {
    setErrors((prev) => ({ ...prev, form: undefined }));
    const errorMessage = await signInWithGoogleEverywhere('login');
    if (errorMessage) {
      setErrors((prev) => ({ ...prev, form: errorMessage }));
    }
  };

  return (
    <main className="auth-screen">
      <form className="auth-card" onSubmit={submit} noValidate>
        <BrandLogo className="auth-card__brand" />
        <h1 className="auth-card__title">{t('auth.login.title')}</h1>
        <p className="auth-card__subtitle">{t('auth.login.subtitle')}</p>

        {inApp ? (
          <p className="auth-card__hint">{t('auth.login.in_app_note')}</p>
        ) : (
          <>
            <GoogleSignInButton onClick={signInWithGoogle}>{t('auth.login.google')}</GoogleSignInButton>

            <span className="auth-card__divider">ან</span>
          </>
        )}

        <Stack gap={4}>
          <TextInput
            label={t('auth.login.email_label')}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
            required
          />
          <PasswordInput
            label={t('auth.login.password_label')}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            required
          />
          {errors.form && (
            <span role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--text-sm)', fontWeight: 700 }}>
              {errors.form}
            </span>
          )}
          <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
            {submitting ? t('auth.login.submit_loading') : t('auth.login.submit')}
          </Button>
        </Stack>

        <p className="auth-card__alt">
          <Link to="/forgot-password">{t('auth.login.forgot_password')}</Link>
        </p>

        <p className="auth-card__alt">
          {t('auth.login.no_account')}
          <Link to="/signup">{t('auth.login.signup_link')}</Link>
        </p>
      </form>
      <div className="auth-screen__legal">
        <LegalLinks />
      </div>
    </main>
  );
}

